import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  Video,
  Image as ImageIcon,
  FileText,
  CheckCircle2,
  Clock,
  Play,
  X,
  Zap,
  Crown,
  Rocket,
  Check,
  Infinity as InfinityIcon,
  Gauge,
  User,
  Wand2,
  Loader2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ─── DATA ─── */

const steps = [
  {
    icon: ImageIcon,
    step: "01",
    title: "Manda a Foto",
    description: "Upload do seu produto. Pode ser do iPhone mesmo.",
  },
  {
    icon: FileText,
    step: "02",
    title: "Conta Pra Gente",
    description: "Nome, descrição e o tom que você quer.",
  },
  {
    icon: Video,
    step: "03",
    title: "Recebe 3 Publis",
    description: "Em minutos, 3 vídeos prontos. Escolhe e posta.",
  },
];

const comparison = [
  { aspect: "Custo por publi", before: "R$ 500 ~ R$ 5.000", after: "R$ 5,58" },
  { aspect: "Tempo de entrega", before: "3 a 14 dias", after: "~5 minutos" },
  { aspect: "Controle criativo", before: "Nenhum", after: "Total" },
  { aspect: "Escalabilidade", before: "1 por vez", after: "Ilimitada" },
  { aspect: "Ghosting", before: "Frequente", after: "Impossível" },
  { aspect: "Formato pronto", before: "Às vezes", after: "9:16 sempre" },
];

const plans = [
  {
    name: "Avulso",
    description: "Teste sem compromisso",
    price: "27,90",
    credits: 5,
    videos: 15,
    icon: Zap,
    features: [
      "5 créditos (15 vídeos)",
      "Créditos sem validade",
      "3 vídeos por crédito",
      "Download por 7 dias",
    ],
    cta: "Quero Testar",
    ctaVariant: "outline" as const,
  },
  {
    name: "Mensal",
    description: "Pra quem escala de verdade",
    price: "197,90",
    priceLabel: "/mês",
    credits: 30,
    videos: 90,
    icon: Crown,
    popular: true,
    features: [
      "30 créditos/mês (90 vídeos)",
      "Renovação automática",
      "3 vídeos por crédito",
      "Suporte prioritário",
    ],
    cta: "Quero Escalar",
    ctaVariant: "hero" as const,
  },
  {
    name: "Anual",
    description: "Economia de 16%",
    price: "1.997,90",
    priceLabel: "/ano",
    monthlyEquivalent: "166,49",
    credits: 360,
    videos: 1080,
    icon: Rocket,
    features: [
      "360 créditos/ano (1080 vídeos)",
      "30 créditos por mês",
      "3 vídeos por crédito",
      "Suporte prioritário",
    ],
    cta: "Quero Economizar",
    ctaVariant: "hero" as const,
  },
];

const faqs = [
  {
    question: "Como funcionam os créditos?",
    answer:
      "1 crédito = 1 task = 3 vídeos UGC diferentes. Manda foto + descrição e recebe 3 publis prontas.",
  },
  {
    question: "As pessoas vão saber que é IA?",
    answer:
      "Não. Nossos avatares são hiper-realistas. Ninguém vai perceber — nem sua mãe.",
  },
  {
    question: "Posso pedir reembolso?",
    answer: "Sim, até 7 dias após a compra. Sem burocracia.",
  },
  {
    question: "E se a task falhar?",
    answer: "Reprocessa de graça. A gente não lucra com erro.",
  },
];

const stats = [
  { icon: InfinityIcon, value: "∞", label: "Influencers" },
  { icon: Gauge, value: "10x", label: "Mais Rápido" },
  { icon: Clock, value: "~5min", label: "Por Publi" },
];

/* ─── GENERATION TIMELINE STEPS (for landing showcase) ─── */
const PIPELINE_STEPS = [
  { id: "pending", label: "Na fila", icon: Sparkles },
  { id: "moderating", label: "Validação", icon: Zap },
  { id: "processing", label: "Criando Persona", icon: User },
  { id: "generating_image", label: "Gerando Imagem", icon: ImageIcon },
  { id: "generating_video", label: "Gerando Vídeos", icon: Video },
  { id: "done", label: "Pronto!", icon: CheckCircle2 },
];

const stagger = {
  container: {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
  },
  item: {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  },
};

/* ─── GENERATION SHOWCASE COMPONENT ─── */
function GenerationShowcase() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % PIPELINE_STEPS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-2">
      {PIPELINE_STEPS.map((step, i) => {
        const Icon = step.icon;
        const status =
          i < activeStep ? "done" : i === activeStep ? "active" : "waiting";

        return (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08, duration: 0.3 }}
            className="relative"
          >
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-500 ${status === "active"
                ? "bg-primary/10 border border-primary/30"
                : status === "done"
                  ? "bg-primary/5 opacity-60"
                  : "opacity-30"
                }`}
            >
              {/* Icon */}
              <div
                className={`relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${status === "active"
                  ? "bg-primary text-primary-foreground"
                  : status === "done"
                    ? "bg-primary/20 text-primary"
                    : "bg-secondary text-muted-foreground"
                  }`}
              >
                {status === "done" ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : status === "active" ? (
                  <>
                    <Icon className="w-4 h-4" />
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-primary"
                      animate={{
                        scale: [1, 1.4, 1],
                        opacity: [0.5, 0, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>

              {/* Label */}
              <span
                className={`text-xs font-medium ${status === "active" ? "text-primary" : "text-muted-foreground"
                  }`}
              >
                {step.label}
              </span>

              {/* Spinner */}
              {status === "active" && (
                <Loader2 className="w-3 h-3 text-primary animate-spin ml-auto flex-shrink-0" />
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function Index() {
  return (
    <Layout>
      {/* ─── HERO ─── */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 relative overflow-hidden min-h-[85vh] flex items-center">
        {/* Background — pure void with subtle gold mist */}
        <div className="absolute inset-0 gradient-mist-gold" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDIxNSwwLDAuMDIpIi8+PC9zdmc+')] opacity-40" />

        <div className="container mx-auto px-4 relative">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-6 items-center">
            {/* Left: Copy — 60% */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-[3] text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium text-primary">
                  Publis com IA · Sem influencer, sem drama
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 tracking-tight">
                Chega de pagar cachê{" "}
                <span className="text-gradient">
                  pra quem some depois do pix
                </span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Gere publis hiper-realistas com IA em minutos. Seu produto +
                nossa inteligência = vídeos UGC prontos pra Reels, TikTok e
                Shorts.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10">
                <Button variant="hero" size="lg" asChild>
                  <Link to="/cadastro">
                    Criar Minha Primeira Publi
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="ghost" size="lg" asChild>
                  <Link to="#como-funciona">
                    <Play className="w-4 h-4" />
                    Ver Como Funciona
                  </Link>
                </Button>
              </div>

              {/* Inline stats */}
              <div className="flex items-center justify-center lg:justify-start gap-6 sm:gap-10">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-gradient">
                      {stat.value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Demo Video — 40%, floating */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="flex-[2] relative"
            >
              <div className="aspect-[9/16] max-w-[260px] sm:max-w-[300px] mx-auto rounded-3xl gradient-card-depth border border-border overflow-hidden relative shadow-z3">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/60 z-10 pointer-events-none" />
                <video
                  src="/video_demo.mp4"
                  poster="/imagem_demo.png"
                  className="w-full h-full object-cover"
                  playsInline
                  preload="metadata"
                  muted
                  loop
                  autoPlay
                />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 glass px-4 py-2 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-primary animate-aura-breathe" />
                  <span className="text-xs text-muted-foreground font-medium">
                    Publi gerada por IA
                  </span>
                </div>
              </div>
              {/* Gold glow only */}
              <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-gold-primary/5 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-44 h-44 rounded-full bg-gold-primary/5 blur-3xl pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── GENERATION ANIMATION SHOWCASE ─── */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute inset-0 gradient-void" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Veja a <span className="text-gradient">mágica acontecer</span>
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              É assim que sua publi nasce. Da foto do produto ao vídeo pronto.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
            {/* Left: Generation timeline animation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-6 rounded-2xl gradient-card-depth border border-border"
            >
              <div className="flex items-center gap-2 mb-4">
                <Wand2 className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-primary">
                  Simulação em tempo real
                </span>
              </div>
              <GenerationShowcase />
            </motion.div>

            {/* Right: Result preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="space-y-4"
            >
              <div className="p-5 rounded-xl bg-void-soft border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                    <Video className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">3 vídeos prontos</p>
                    <p className="text-xs text-muted-foreground">
                      9:16 · Reels · TikTok · Shorts
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="aspect-[9/16] rounded-lg bg-void-warm border border-border flex items-center justify-center"
                    >
                      <Play className="w-5 h-5 text-muted-foreground/40" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-lg bg-void-soft border border-border">
                  <p className="text-lg font-bold text-gradient">~5min</p>
                  <p className="text-[10px] text-muted-foreground">Geração</p>
                </div>
                <div className="p-3 rounded-lg bg-void-soft border border-border">
                  <p className="text-lg font-bold text-gradient">3</p>
                  <p className="text-[10px] text-muted-foreground">Vídeos</p>
                </div>
                <div className="p-3 rounded-lg bg-void-soft border border-border">
                  <p className="text-lg font-bold text-gradient">9:16</p>
                  <p className="text-[10px] text-muted-foreground">Formato</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── COMO FUNCIONA ─── */}
      <section id="como-funciona" className="py-16 md:py-24 bg-void-deep relative">
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              <span className="text-gradient">3 Passos</span> e Sua Publi Tá
              Pronta
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Mais fácil que pedir comida no app.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 md:gap-10 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="relative text-center"
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden sm:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary/30 to-transparent" />
                )}
                <div className="relative inline-block mb-5">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-void-soft border border-border flex items-center justify-center">
                    <step.icon className="w-8 h-8 md:w-10 md:h-10 text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 md:w-8 md:h-8 rounded-lg gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BEFORE / AFTER ─── */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Influencer tradicional{" "}
              <span className="text-gradient">vs Publee</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Compare e tire suas conclusões.
            </p>
          </motion.div>

          {/* Comparison table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <div className="rounded-xl border border-border overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-3 bg-void-soft">
                <div className="p-4 text-sm font-medium text-muted-foreground" />
                <div className="p-4 text-center">
                  <span className="text-sm font-semibold text-muted-foreground">
                    Influencer
                  </span>
                </div>
                <div className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm font-semibold text-primary">
                      Publee
                    </span>
                  </div>
                </div>
              </div>

              {/* Rows */}
              {comparison.map((row, index) => (
                <motion.div
                  key={row.aspect}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                  className={`grid grid-cols-3 border-t border-border ${index % 2 === 0 ? "bg-void-absolute" : "bg-void-deep"
                    }`}
                >
                  <div className="p-4 text-sm font-medium text-foreground flex items-center">
                    {row.aspect}
                  </div>
                  <div className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center">
                    <span className="flex items-center gap-1.5">
                      <X className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
                      {row.before}
                    </span>
                  </div>
                  <div className="p-4 text-center text-sm font-medium text-primary flex items-center justify-center">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      {row.after}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="mt-5 text-center"
            >
              <p className="text-xs text-muted-foreground">
                Custo calculado com base no plano Avulso (R$ 27,90 ÷ 5
                créditos).
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="precos" className="py-16 md:py-24 bg-void-deep relative">
        <div className="absolute inset-0 gradient-mist-gold pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Preços que{" "}
              <span className="text-gradient">cabem no bolso</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Sem contrato, sem surpresa na fatura. Cada crédito gera 3 vídeos.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`relative rounded-2xl p-7 border transition-all duration-300 ${plan.popular
                  ? "gradient-card-depth border-primary glow-gold md:scale-105"
                  : "bg-void-soft border-border hover:border-primary/30"
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full gradient-primary text-xs font-semibold text-primary-foreground">
                      <Crown className="w-3 h-3" />
                      Mais Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mx-auto mb-3">
                    <plan.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-sm text-muted-foreground">R$</span>
                    <span className="text-3xl font-bold">{plan.price}</span>
                    {plan.priceLabel && (
                      <span className="text-muted-foreground text-sm">
                        {plan.priceLabel}
                      </span>
                    )}
                  </div>
                  {plan.monthlyEquivalent && (
                    <p className="text-xs text-muted-foreground mt-1">
                      ≈ R$ {plan.monthlyEquivalent}/mês
                    </p>
                  )}
                  <p className="text-xs text-primary mt-2 font-medium">
                    {plan.credits} créditos = {plan.videos} vídeos
                  </p>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2.5 text-sm"
                    >
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.ctaVariant}
                  className="w-full"
                  size="lg"
                  asChild
                >
                  <Link to="/cadastro">{plan.cta}</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Perguntas <span className="text-gradient">Frequentes</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-void-soft border border-border rounded-xl px-5 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-medium text-sm">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4 text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-6">
              <Link
                to="/precos"
                className="text-sm text-primary hover:underline"
              >
                Ver todas as perguntas →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA FINAL ─── */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-void" />
        <div className="absolute inset-0 gradient-mist-gold" />

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 leading-tight">
              Enquanto você pensa,{" "}
              <span className="text-gradient">
                seu concorrente já postou
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Crie sua conta grátis e gere sua primeira publi em 5 minutos.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button variant="hero" size="lg" asChild>
                <Link to="/cadastro">
                  Começar Agora — É Grátis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/precos">Ver Planos Detalhados</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
